import {
  ArrowLeft,
  BarChart3,
  BookOpen,
  CalendarDays,
  ChevronDown,
  ChevronRight,
  Banknote,
  Dumbbell,
  LayoutDashboard,
  Package,
  Salad,
  Settings,
  Target,
  Wrench,
  HelpCircle,
  LifeBuoy,
  Megaphone,
  ScanLine,
  Search,
  ShoppingCart,
  Tags,
  UserCog,
  Users,
  X,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { faqHintForPath } from "@/lib/faqRouteHints";
import { faqModuleForPath } from "@/lib/faqModules";
import { scoreItem, searchTerms } from "@/lib/faqSearch";
import { FaqAnswer, Highlight } from "@/components/faq/FaqAnswer";
import { useFaqStore } from "@/lib/stores/faqStore";
import { cn } from "@/lib/utils/cn";

interface FaqItem {
  /** Stable id ("<module>.<slug>") used by contextual help hints. */
  id?: string;
  q: string;
  a: string;
  /** Module · section label, shown on cross-module search results. */
  tag?: string;
}
interface FaqSection {
  title: string;
  items: FaqItem[];
}
interface FaqModule {
  name: string;
  description: string;
  title: string;
  subtitle: string;
  sections: FaqSection[];
}

function FaqRow({
  item,
  id,
  expanded,
  terms,
  flash,
  onToggle,
}: {
  item: FaqItem;
  id: string;
  expanded: boolean;
  terms: string[];
  flash?: boolean;
  onToggle: () => void;
}) {
  return (
    <div
      className={cn(
        "rounded-xl border transition-all duration-300 motion-reduce:transition-none",
        flash && "ring-2 ring-primary-400 ring-offset-2 ring-offset-surface",
        expanded
          ? "border-primary-300/60 bg-primary-50/50 shadow-[0_4px_14px_-6px_rgb(var(--color-primary-500)/0.35)] dark:border-primary-500/30 dark:bg-primary-500/[0.07]"
          : "border-black/[0.07] bg-white/70 hover:border-primary-300/50 hover:bg-white hover:shadow-sm dark:border-white/[0.09] dark:bg-white/[0.03] dark:hover:bg-white/[0.06]",
      )}
    >
      <button
        type="button"
        id={`${id}-btn`}
        aria-expanded={expanded}
        aria-controls={`${id}-panel`}
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-3 rounded-xl px-4 py-3 text-start focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
      >
        <span className="min-w-0">
          {item.tag && (
            <span className="mb-0.5 block truncate text-[10px] font-semibold uppercase tracking-wider text-primary-600 dark:text-primary-300">
              {item.tag}
            </span>
          )}
          <span className="text-sm font-medium leading-snug text-ink-900 dark:text-ink-100">
            <Highlight text={item.q} terms={terms} />
          </span>
        </span>
        <ChevronDown
          aria-hidden="true"
          className={cn(
            "h-4 w-4 flex-shrink-0 text-ink-400 transition-transform duration-300 ease-out motion-reduce:transition-none",
            expanded && "rotate-180 text-primary-600",
          )}
        />
      </button>
      {/* grid-rows 0fr -> 1fr animates height without measuring */}
      <div
        id={`${id}-panel`}
        role="region"
        aria-labelledby={`${id}-btn`}
        className={cn(
          "grid transition-[grid-template-rows,opacity] duration-300 ease-out motion-reduce:transition-none",
          expanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
        )}
      >
        <div className="overflow-hidden">
          <div className="mx-4 border-t border-primary-200/40 pb-4 pt-3 dark:border-white/[0.08]">
            <FaqAnswer text={item.a} terms={terms} />
          </div>
        </div>
      </div>
    </div>
  );
}


const MODULE_ICONS: Record<string, LucideIcon> = {
  dashboard: LayoutDashboard,
  members: Users,
  checkin: ScanLine,
  memberships: Tags,
  leads: Target,
  classes: CalendarDays,
  training: Dumbbell,
  nutrition: Salad,
  staff: UserCog,
  hr: Banknote,
  pos: ShoppingCart,
  inventory: Package,
  operations: Wrench,
  marketing: Megaphone,
  reports: BarChart3,
  settings: Settings,
};

/**
 * Universal, non-modal FAQ popup docked to the side of the app. Mounted once in
 * AppShell, so it stays open while the user navigates between modules.
 *  - Home view: a card per module + search across every module.
 *  - Module view: that module's FAQs, with a back button to the home view.
 * Opening it (or navigating) while on a module's page jumps straight to that
 * module. Content: public/locales/<lng>/faq.json -> "modules".
 */
export function FaqDock() {
  const { t } = useTranslation("faq");
  const { pathname } = useLocation();
  const { open, module, hint, auto, setAuto, openFor, setModule, goHome, close, clearHint, suggest } = useFaqStore();
  const [flashId, setFlashId] = useState<string | null>(null);
  const pendingHint = useRef<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const modules = t("modules", { returnObjects: true, defaultValue: {} }) as unknown as Record<string, FaqModule>;
  const keys = useMemo(
    () =>
      Object.keys(modules && typeof modules === "object" ? modules : {}).filter((k) =>
        Array.isArray(modules[k]?.sections),
      ),
    [modules],
  );

  // Follow the page: entering a module that has FAQs switches the dock to it.
  const routeModule = faqModuleForPath(pathname);
  useEffect(() => {
    if (auto && routeModule) setModule(routeModule);
  }, [auto, routeModule, setModule]);

  // Task pages (new incident, stock-take, campaign, ...) suggest their related question on arrival;
  // leaving for a page without one drops the stale suggestion.
  useEffect(() => {
    if (!auto) return;
    const id = faqHintForPath(pathname);
    if (id) suggest(id);
    else clearHint();
  }, [auto, pathname, suggest, clearHint]);

  // Esc closes the dock when focus is inside it.
  const [panelEl, setPanelEl] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (!open || !panelEl) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && panelEl.contains(document.activeElement)) close();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, panelEl, close]);

  const active = module && keys.includes(module) ? module : null; // null = home view
  const current = active ? modules[active] : null;
  const needle = query.trim();
  const terms = useMemo(() => searchTerms(query), [query]);

  // Module view filters one module; home view searches every module.
  const filtered = useMemo<FaqSection[]>(() => {
    // Every meaningful word must match; most relevant first.
    const rank = (items: FaqItem[]) =>
      items
        .map((i) => ({ i, score: scoreItem(i, terms) }))
        .filter((x) => x.score > 0)
        .sort((x, y) => y.score - x.score);

    if (current) {
      if (!needle) return current.sections;
      return current.sections
        .map((s) => {
          const r = rank(s.items);
          return { section: { ...s, items: r.map((x) => x.i) }, best: r[0]?.score ?? 0 };
        })
        .filter((x) => x.best > 0)
        .sort((x, y) => y.best - x.best)
        .map((x) => x.section);
    }
    if (!needle) return [];
    // Home view: one flat list across all modules, best matches first.
    const all = keys.flatMap((k) =>
      modules[k].sections.flatMap((s) =>
        rank(s.items).map((x) => ({ ...x, item: { ...x.i, tag: `${modules[k].name} · ${s.title}` } })),
      ),
    );
    all.sort((x, y) => y.score - x.score);
    return all.length ? [{ title: t("bestMatches"), items: all.slice(0, 40).map((x) => x.item) }] : [];
  }, [current, needle, terms, keys, modules, t]);
  const total = filtered.reduce((n, s) => n + s.items.length, 0);
  const showHomeGrid = !current && !needle;

  // Switching view resets search and open answers (except a pending contextual hint).
  useEffect(() => {
    setQuery("");
    setExpanded(new Set(pendingHint.current ? [pendingHint.current] : []));
  }, [active]);

  // Contextual help: a hint (from an error toast or an open wizard) makes the dock
  // jump to that question, expand it, scroll to it and flash it.
  useEffect(() => {
    if (!open || !hint) return;
    const mod = hint.id.split(".")[0];
    pendingHint.current = hint.id;
    if (keys.includes(mod)) setModule(mod);
    setQuery("");
    setExpanded(new Set([hint.id]));
    setFlashId(hint.id);
    clearHint();
    const reset = setTimeout(() => {
      pendingHint.current = null;
    }, 0);
    return () => clearTimeout(reset);
  }, [open, hint, keys, setModule, clearHint]);

  useEffect(() => {
    if (!flashId || !open) return;
    // Scroll ONLY the popup's own list (never the page) and put the question near the top,
    // so the section it belongs to stays in view instead of landing mid-list.
    const scroll = setTimeout(() => {
      const list = listRef.current;
      const row = document.getElementById(`${flashId}-btn`);
      if (!list || !row) return;
      const top = row.getBoundingClientRect().top - list.getBoundingClientRect().top + list.scrollTop - 56;
      list.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    }, 350); // after the panel's slide-in and the module switch have rendered
    const stop = setTimeout(() => setFlashId(null), 3000);
    return () => {
      clearTimeout(scroll);
      clearTimeout(stop);
    };
  }, [flashId, open, active]);

  if (keys.length === 0) return null;

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const countOf = (k: string) => modules[k].sections.reduce((n, s) => n + s.items.length, 0);

  return (
    <>
      {/* Launcher — available on every page */}
      <button
        type="button"
        onClick={() => openFor(auto ? routeModule : null)}
        aria-label={t("button")}
        className={cn(
          "fixed bottom-[calc(5.25rem+var(--toolbar-inset,0px))] end-3 z-40 flex h-10 w-10 items-center justify-center rounded-full md:bottom-6 md:end-6 md:h-12 md:w-12",
          "bg-gradient-to-b from-primary-500 to-primary-600 text-white shadow-lg shadow-primary-600/30",
          "transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-500/30",
          open ? "pointer-events-none scale-75 opacity-0" : "scale-100 opacity-100",
          hint && !open && "faq-bounce",
        )}
      >
        <HelpCircle className="h-5 w-5 md:h-6 md:w-6" />
        {hint && !open && (
          <span className="absolute -end-0.5 -top-0.5 h-3.5 w-3.5 rounded-full border-2 border-white bg-accent-500" aria-hidden="true" />
        )}
      </button>

      <aside
        ref={setPanelEl}
        role="complementary"
        aria-label={t("button")}
        aria-hidden={!open}
        className={cn(
          "fixed z-40 flex flex-col overflow-hidden rounded-2xl border border-black/[0.08] bg-surface shadow-elev-3 dark:border-white/[0.1]",
          "end-3 bottom-[calc(4.75rem+var(--toolbar-inset,0px))] h-[min(28rem,62dvh)] w-[min(22rem,calc(100vw-1.5rem))] md:bottom-4 md:end-4 md:top-20 md:h-auto md:w-[26rem]",
          "transition-[transform,opacity,visibility] duration-300 ease-out motion-reduce:transition-none",
          open ? "visible translate-x-0 opacity-100" : "invisible opacity-0 ltr:translate-x-8 rtl:-translate-x-8",
        )}
      >
        <div className="border-b border-border-200 bg-gradient-to-b from-primary-50/80 to-transparent px-3 pb-2.5 pt-2.5 dark:from-primary-500/10 md:px-5 md:pb-4 md:pt-4">
          <div className="flex items-center justify-between gap-2">
            {current ? (
              <button
                type="button"
                onClick={goHome}
                className="-ms-2 inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-100/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:text-primary-300 dark:hover:bg-primary-500/15"
              >
                <ArrowLeft className="h-3.5 w-3.5 rtl:rotate-180" aria-hidden="true" />
                {t("back")}
              </button>
            ) : (
              <span />
            )}
            <div className="flex items-center gap-1.5">
              {/* Smart help on/off — hover for what it does */}
              <button
                type="button"
                role="switch"
                aria-checked={auto}
                title={auto ? t("smartHelpOn") : t("smartHelpOff")}
                onClick={() => setAuto(!auto)}
                className="group flex items-center gap-2 rounded-full py-1 pe-1 ps-2 text-xs font-medium text-ink-700 transition-colors hover:bg-black/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:hover:bg-white/10"
              >
                <span className={auto ? "text-primary-700 dark:text-primary-300" : "text-ink-500"}>{t("smartHelp")}</span>
                <span
                  className={cn(
                    "relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors",
                    auto ? "bg-primary-500" : "bg-ink-300/50",
                  )}
                >
                  <span
                    className={cn(
                      "pointer-events-none absolute top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-all duration-200",
                      auto ? "start-[1.125rem]" : "start-0.5",
                    )}
                  />
                </span>
              </button>
              <button
                type="button"
                onClick={close}
                aria-label={t("close")}
                className="rounded-full p-1.5 text-ink-400 transition-colors hover:bg-black/5 hover:text-ink-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:hover:bg-white/10"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="mt-1.5 flex items-start gap-2.5 md:mt-2 md:gap-3">
            <div className="flex h-8 w-8 flex-shrink-0 md:h-10 md:w-10 items-center justify-center rounded-xl border border-primary-200/60 bg-primary-100/80 shadow-[inset_0_1px_3px_rgba(0,0,0,0.06)] dark:border-primary-500/20 dark:bg-primary-500/15">
              {(() => {
                const Icon = (active && MODULE_ICONS[active]) || LifeBuoy;
                return <Icon className="h-5 w-5 text-primary-600 dark:text-primary-400" />;
              })()}
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-sm font-semibold leading-tight text-ink-900 dark:text-ink-100 max-md:line-clamp-2 md:text-base">
                {current ? current.title : t("homeTitle")}
              </h2>
              <p className="mt-1 text-xs text-ink-500 max-md:hidden">{current ? current.subtitle : t("homeSubtitle")}</p>
            </div>
          </div>

          <div className="relative mt-2 md:mt-3">
            <Search
              className="pointer-events-none absolute start-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400"
              aria-hidden="true"
            />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={current ? t("search") : t("searchAll")}
              aria-label={current ? t("search") : t("searchAll")}
              className="h-9 w-full rounded-xl border border-black/[0.08] bg-white pe-9 ps-10 text-base md:h-10 md:text-sm text-ink-900 shadow-[inset_0_1px_2px_rgba(0,0,0,0.05)] transition-shadow placeholder:text-ink-400 focus:border-primary-400 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-white/[0.1] dark:bg-white/[0.05] dark:text-ink-100"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                aria-label={t("clear")}
                className="absolute end-2.5 top-1/2 -translate-y-1/2 rounded-full p-1 text-ink-400 transition-colors hover:bg-black/5 hover:text-ink-700 dark:hover:bg-white/10"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          {!showHomeGrid && (
            <p className="mt-2 text-xs text-ink-500" aria-live="polite">
              {t("resultCount", { count: total })}
            </p>
          )}

        </div>

        {/* key forces a fresh enter animation when switching between home and a module */}
        <div ref={listRef} key={active ?? "home"} className="faq-fade flex-1 space-y-4 overflow-y-auto px-3 py-3 md:space-y-6 md:px-5 md:py-5">
          {showHomeGrid && (
            <div className="grid grid-cols-1 gap-2.5">
              {keys.map((k, i) => {
                const Icon = MODULE_ICONS[k] ?? BookOpen;
                const count = countOf(k);
                const soon = count === 0;
                return (
                  <button
                    key={k}
                    type="button"
                    disabled={soon}
                    onClick={() => setModule(k)}
                    style={{ animationDelay: `${Math.min(i, 8) * 40}ms` }}
                    className={cn(
                      "faq-rise group flex items-center gap-3 rounded-xl border px-3.5 py-3 text-start transition-all duration-200",
                      "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
                      soon
                        ? "cursor-not-allowed border-black/[0.05] bg-ink-200/20 opacity-60 dark:border-white/[0.06]"
                        : "border-black/[0.07] bg-white/70 hover:-translate-y-0.5 hover:border-primary-300/60 hover:bg-white hover:shadow-md dark:border-white/[0.09] dark:bg-white/[0.03] dark:hover:bg-white/[0.07]",
                      k === routeModule && !soon && "border-primary-300/70 ring-1 ring-primary-300/40",
                    )}
                  >
                    <span
                      className={cn(
                        "flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg transition-colors",
                        soon
                          ? "bg-ink-200/40 text-ink-400"
                          : "bg-primary-50 text-primary-600 group-hover:bg-primary-100 dark:bg-primary-500/15 dark:text-primary-300",
                      )}
                    >
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-ink-900 dark:text-ink-100">{modules[k].name}</span>
                        {k === routeModule && !soon && (
                          <span className="rounded-full bg-primary-500 px-1.5 py-0.5 text-[10px] font-medium text-white">
                            {t("currentPage")}
                          </span>
                        )}
                      </span>
                      <span className="mt-0.5 line-clamp-2 block text-xs text-ink-500">{modules[k].description}</span>
                    </span>
                    {soon ? (
                      <span className="flex-shrink-0 rounded-full bg-ink-200/60 px-2 py-0.5 text-[10px] font-medium text-ink-500 dark:bg-white/10">
                        {t("comingSoon")}
                      </span>
                    ) : (
                      <span className="flex flex-shrink-0 items-center gap-1 text-xs text-ink-400">
                        {t("articleCount", { count })}
                        <ChevronRight
                          className="h-4 w-4 transition-transform group-hover:translate-x-0.5 rtl:rotate-180 rtl:group-hover:-translate-x-0.5"
                          aria-hidden="true"
                        />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {!showHomeGrid && filtered.length === 0 && (
            <div className="faq-fade flex flex-col items-center gap-2 py-12 text-center">
              <Search className="h-8 w-8 text-ink-300" aria-hidden="true" />
              <p className="text-sm font-medium text-ink-700 dark:text-ink-200">{t("noResults")}</p>
              <p className="text-xs text-ink-500">{t("noResultsHint")}</p>
            </div>
          )}

          {!showHomeGrid &&
            filtered.map((section, si) => (
              <section
                key={`${active}-${section.title}`}
                className="faq-rise space-y-2.5"
                style={{ animationDelay: `${Math.min(si, 6) * 50}ms` }}
              >
                <h3 className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-ink-500">
                  <span className="h-px w-4 flex-shrink-0 bg-primary-400/60" aria-hidden="true" />
                  <span className="min-w-0">{section.title}</span>
                  <span className="rounded-full bg-ink-200/50 px-1.5 py-0.5 text-[10px] font-medium dark:bg-white/10">
                    {section.items.length}
                  </span>
                </h3>
                <div className="space-y-2">
                  {section.items.map((item, ii) => {
                    const id = item.id ?? `faq-${active ?? "all"}-${si}-${ii}`;
                    return (
                      <FaqRow
                        key={item.q}
                        id={id}
                        item={item}
                        terms={terms}
                        flash={flashId === item.id}
                        expanded={needle !== "" || expanded.has(id)}
                        onToggle={() => toggle(id)}
                      />
                    );
                  })}
                </div>
              </section>
            ))}
        </div>
      </aside>
    </>
  );
}
